<template>
  <ValidationProvider
    :ref="validationProviderRef"
    :debounce="validationDelay"
    :mode="veeValidateInteractionMode"
    :name="label"
    :rules="rules"
  >
    <div :class="isInProcedure ? 'file-input__containerProc' : 'file-input__container'">
      <v-file-input
        :id="validationProviderRef"
        ref="file-input"
        v-model="mutableValue"
        v-bind="$attrs"
        :loading="isLoading"
        :accept="accept"
        :disabled="isDisabled"
        :label="displayedLabel"
        :rules="vFileRules"
        :dense="dense"
        :hideDetails="hideDetails"
        :data-cy="cyData"
        :appendIcon="null"
        outlined
        class="file-input__input"
        :prependIcon="iconLeft"
        @change="handleChange"
        @click:clear="$emit('click:clear')"
        @click:append-outer="onClickAppendIcon"
      >
        <template v-slot:selection="{ }">
          <div
            style="overflow-wrap: anywhere; display: flex; flex-direction: column; margin-bottom: 0 !important;"
          >
            <span>
              {{
                isFileInstance(mutableValue)
                  ? mutableValue.name
                  : mutableValue && mutableValue.fileName
                    ? mutableValue.fileName
                    : ''
              }}
            </span>
            <span v-if="isFileInstance(mutableValue) || (mutableValue && mutableValue.size)">
              {{ ' (' + formatFileSize(mutableValue.size) + ')' }}
            </span>
          </div>
        </template>
        <template v-slot:append>
          <v-icon
            v-if="iconPreviewFile"
            :disabled="isDisabled || !mutableValue"
            @click="onClickEyeIcon(mutableValue)"
          >
            mdi-eye
          </v-icon>
        </template>
      </v-file-input>
    </div>
  </ValidationProvider>
</template>

<script>
import prettyBytes from 'pretty-bytes';
import { ValidationProvider } from 'vee-validate';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';

export default {
    components: {
        ValidationProvider,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: '',
        },
        rules: {
            type: Object,
            default: () => ({}),
        },
        value: {
            type: [File] || Object,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        cyData: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        isInProcedure: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: Boolean,
            default: false,
        },
        iconLeft: {
            type: String,
            default: 'mdi-upload',
        },
        iconPreviewFile: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mutableValue: this.value,
            hasChanged: false,
            objectFileConverted: null,
        };
    },
    computed: {
        /**
         * Have to follow the HTML standard
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
         */
        accept() {
            const { mimes } = this.rules;

            return Array.isArray(mimes) ? mimes.join(',') : null;
        },
        displayedLabel() {
            return `${this.label}${this.isRequired ? ' *' : ''}`;
        },
        isRequired() {
            return this.rules.required === true;
        },
        /**
         * We don't want to see the icon when the user just picked a file, we can't open it in a new tab to see it.
         * Only in edition mode, when the file has been uploaded already.
         */
        appendIcon() {
            return this.isEdition && this.mutableValue !== null && !this.hasChanged ? 'mdi-eye' : null;
        },
        /**
         * Specific rule for vuetify.
         *
         * @return {Array}
         */
        vFileRules() {
            const vFileRules = [];
            const size = parseInt(this.rules.size, 10);

            if (!Number.isNaN(size)) {
                vFileRules.push(this.validSize.bind(this, size));
            }

            return vFileRules;
        },
        isDisabled() {
            if (this.disabled || this.readonly) {
                return true;
            }
            return false;
        },
    },
    watch: {
        value: {
            handler() {
                this.mutableValue = this.value;
            },
            immediate: true,
        },
    },
    methods: {
        handleChange(e) {
            this.$emit('input', e);
            this.hasChanged = true;
        },
        onClickAppendIcon() {
            if (this.mutableValue === null) {
                return;
            }
            const file = this.mutableValue;
            this.$utils.saveFileOnDisk(file, file.name);
        },
        /**
         * The error message for size is not working ("NaN undefined") with vee-validate,
         * so we make our own with vuetify.
         * https://vuetifyjs.com/en/components/file-inputs#validation
         *
         * The size rule for vee-validate is in kilobytes.
         * https://logaretm.github.io/vee-validate/guide/rules.html#size
         *
         * The size we have in value is in bytes.
         *
         * @param {Number} maxSize - Max authorized size in kilobytes
         * @param {File} value
         * @return {Boolean}
         */
        validSize(maxSize, value) {
            const locale = this.$store.getters.lang;
            const maxSizeBytes = maxSize * 1024;
            const maxSizePretty = prettyBytes(maxSizeBytes, { locale });

            return (
                !value
                || value.size <= maxSizeBytes
                || this.$t('forms.errors.fileSize', { maxSize: maxSizePretty })
            );
        },
        isFileInstance(value) {
            return value instanceof File;
        },
        formatFileSize(size) {
            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            let unitIndex = 0;
            let formattedSize = size;

            while (formattedSize >= 1024 && unitIndex < units.length - 1) {
                formattedSize /= 1024;
                unitIndex += 1;
            }

            return `${formattedSize.toFixed(2)} ${units[unitIndex]}`;
        },
        onClickEyeIcon(item) {
            this.$utils.saveFileOnDisk(item, item.name);
        },
    },
};
</script>

<style lang="scss" scoped>
    .file-input__container {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    .file-input__view-icon {
        right: 0;
    }


    .file-input__containerProc {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    @media screen and (max-width: 1000px) {
        .file-input__containerProc {
            margin-left: -33px;
            margin-right: -40px;
            flex-direction: row;
            display: flex;
        }
    }
</style>
